import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  Button, 
  Space, 
  Spin, 
  Modal,
  Image,
  message,
  Tooltip
} from 'antd';
import {
  CameraOutlined,
  EnvironmentOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
  SwapOutlined
} from '@ant-design/icons';
import CameraCapture from '../../utils/cameraUtils';
import GPSTracker from '../../utils/gpsUtils';
import staffAuthManager from '../../firebase/staffAuthService';
import attendanceManager from '../../firebase/attendanceService';
import './AttendanceCapture.css';

const AttendanceCapture = ({ onCapture, attendanceType = 'clock-in' }) => {
  // Camera states
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [photoMetadata, setPhotoMetadata] = useState(null);
  
  // GPS states
  const [locationReady, setLocationReady] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [warehouseProximity, setWarehouseProximity] = useState(null);
  
  // UI states
  const [loading, setLoading] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [validationResults, setValidationResults] = useState(null);
  const [debugInfo, setDebugInfo] = useState([]);
  
  // Camera state management
  const [cameraState, setCameraState] = useState('initializing'); // 'initializing', 'ready', 'error', 'recovering'
  const [recoveryAttempts, setRecoveryAttempts] = useState(0);
  const [isRecovering, setIsRecovering] = useState(false);
  
  
  // Mirror states - Initialize for front camera (both clock-in and clock-out use front camera)
  const [mirrorMode, setMirrorMode] = useState(true); // Always true for front camera (both clock-in and clock-out)
  const [currentFacingMode, setCurrentFacingMode] = useState('user'); // Always start with front camera
  
  // Camera load monitoring for debugging
  const [cameraLoadCount, setCameraLoadCount] = useState(0);
  
  // Refs
  const videoRef = useRef(null);
  const cameraRef = useRef(null);
  const gpsRef = useRef(null);
  
  // Mock warehouse data (this would come from Firebase in real implementation)
  const warehouseLocations = useMemo(() => [
    {
      id: 'warehouse-1',
      name: 'Citilink Warehouse Complex',
      address: '102F Pasir Panjang Rd, #04-12, Singapore - 118530',
      coordinates: {
        latitude: 1.2762970485308482, // Singapore coordinates
        longitude: 103.79506703099759
      },
      radius: 300 // 100 meters
    }
  ], []);

  // Enhanced mobile detection function
  const detectMobileDevice = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobileUserAgent = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isSmallScreen = window.innerWidth <= 768;
    
    // Specific mobile browser detection
    const isIOSSafari = /iphone|ipad|ipod/i.test(userAgent) && /safari/i.test(userAgent) && !/chrome|crios|fxios/i.test(userAgent);
    const isChromeMobile = /android/i.test(userAgent) && /chrome/i.test(userAgent);
    const isFirefoxMobile = /android/i.test(userAgent) && /firefox/i.test(userAgent);
    
    const mobileInfo = {
      isMobile: isMobileUserAgent || (isTouchDevice && isSmallScreen),
      isIOSSafari,
      isChromeMobile,
      isFirefoxMobile,
      isTouchDevice,
      isSmallScreen,
      userAgent: userAgent.substring(0, 100) // Truncated for logging
    };
    
    return mobileInfo;
  };

  // Debug logging function
  const addDebugLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = `[${timestamp}] ${message}`;
    console.log(`${type.toUpperCase()}: ${logEntry}`);
    setDebugInfo(prev => [...prev.slice(-9), { message: logEntry, type, timestamp }]);
  };

  // Video content validation function
  const validateVideoContent = async () => {
    if (!videoRef.current) return false;
    
    // Wait a moment for video to stabilize
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const hasValidContent = videoRef.current.videoWidth > 0 && 
                           videoRef.current.videoHeight > 0 && 
                           videoRef.current.readyState >= 2;
    
    addDebugLog(`Video content validation: ${hasValidContent ? 'Valid' : 'Invalid'} (width: ${videoRef.current.videoWidth}, height: ${videoRef.current.videoHeight}, readyState: ${videoRef.current.readyState})`);
    return hasValidContent;
  };

  // Programmatic nuclear restart function
  const performProgrammaticNuclearRestart = async () => {
    addDebugLog('Performing programmatic nuclear restart...');
    
    try {
      // Reset UI state
      setCameraReady(false);
      setCameraError(null);
      
      // Reset video element
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      
      // Perform nuclear restart
      const result = await cameraRef.current.performNuclearRestart();
      
      if (result.success && videoRef.current) {
        videoRef.current.srcObject = result.stream;
        addDebugLog('Nuclear restart successful, validating video content...');
        
        // Wait for video to load and validate content
        await new Promise(resolve => setTimeout(resolve, 1000));
        const hasValidContent = await validateVideoContent();
        
        if (hasValidContent) {
          setCameraReady(true);
          setCameraError(null);
          addDebugLog('Nuclear restart completed successfully with valid video content');
          
          // Update facing mode and mirror settings
          const facingMode = cameraRef.current.constraints.video.facingMode;
          setCurrentFacingMode(facingMode);
          setMirrorMode(facingMode === 'user');
          
          return true;
        } else {
          addDebugLog('Nuclear restart succeeded but video content still invalid', 'warn');
          return false;
        }
      } else {
        addDebugLog(`Nuclear restart failed: ${result.error}`, 'error');
        setCameraError(result.error || 'Nuclear restart failed');
        return false;
      }
    } catch (error) {
      addDebugLog(`Nuclear restart error: ${error.message}`, 'error');
      setCameraError('Nuclear restart failed');
      return false;
    }
  };

  // Progressive nuclear restart with multiple strategies
  const performProgressiveNuclearRestart = async () => {
    addDebugLog('Starting progressive nuclear restart...');
    
    try {
      const result = await cameraRef.current.performProgressiveNuclearRestart();
      
      if (result.success && videoRef.current) {
        videoRef.current.srcObject = result.stream;
        addDebugLog('Progressive nuclear restart successful, validating video content...');
        
        // Wait for video to load and validate content
        await new Promise(resolve => setTimeout(resolve, 1000));
        const hasValidContent = await validateVideoContent();
        
        if (hasValidContent) {
          setCameraReady(true);
          setCameraError(null);
          addDebugLog('Progressive nuclear restart completed successfully');
          
          // Update facing mode and mirror settings
          const facingMode = cameraRef.current.constraints.video.facingMode;
          setCurrentFacingMode(facingMode);
          setMirrorMode(facingMode === 'user');
          
          return true;
        } else {
          addDebugLog('Progressive nuclear restart succeeded but video content still invalid', 'warn');
          setCameraError('Camera loaded but video display failed. Please try switching camera.');
          return false;
        }
      } else {
        addDebugLog(`Progressive nuclear restart failed: ${result.error}`, 'error');
        setCameraError(result.error || 'All camera initialization strategies failed');
        return false;
      }
    } catch (error) {
      addDebugLog(`Progressive nuclear restart error: ${error.message}`, 'error');
      setCameraError('Camera initialization failed');
      return false;
    }
  };

  useEffect(() => {
    let cleanupResilience;

    const waitForVideoElement = async () => {
      // Wait for video element to be available in DOM
      let attempts = 0;
      const maxAttempts = 50; // 5 seconds max wait
      
      while (!videoRef.current && attempts < maxAttempts) {
        addDebugLog(`Waiting for video element... attempt ${attempts + 1}/${maxAttempts}`);
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }
      
      if (!videoRef.current) {
        throw new Error('Video element not available after waiting');
      }
      
      addDebugLog('Video element is now available');
      return videoRef.current;
    };

    const initServices = async () => {
      setLoading(true);
      addDebugLog('Starting enhanced service initialization...');
      
      // Absolute safety timeout - force loading to stop after 15 seconds no matter what
      const absoluteTimeout = setTimeout(() => {
        addDebugLog('ABSOLUTE TIMEOUT: Forcing loading to stop', 'warn');
        setLoading(false);
      }, 15000);
      
      try {
        // Check browser support first
        const browserSupport = {
          geolocation: !!navigator.geolocation,
          mediaDevices: !!navigator.mediaDevices,
          getUserMedia: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
          isSecureContext: window.isSecureContext
        };
        
        addDebugLog(`Browser support: geolocation=${browserSupport.geolocation}, mediaDevices=${browserSupport.mediaDevices}, getUserMedia=${browserSupport.getUserMedia}, isSecureContext=${browserSupport.isSecureContext}`);
        
        if (!browserSupport.isSecureContext) {
          throw new Error('Secure context required for camera/GPS access. Please use HTTPS.');
        }
        
        if (!browserSupport.getUserMedia) {
          throw new Error('Camera access not supported by this browser.');
        }
        
        if (!browserSupport.geolocation) {
          throw new Error('GPS access not supported by this browser.');
        }
        
        // Wait for video element to be ready
        addDebugLog('Waiting for video element to be ready...');
        const videoElement = await waitForVideoElement();
        
        // Enhanced camera initialization using robust approach
        addDebugLog('Enhanced camera initialization starting...');
        cameraRef.current = new CameraCapture();
        
        // 1) init the camera - always start with front camera for both clock-in and clock-out
        const preferredCamera = 'user'; // Always use front camera for both clock-in and clock-out
        const res = await cameraRef.current.initializeCamera(preferredCamera);
        if (!res.success) {
          addDebugLog(`Camera initialization failed: ${res.error}`, 'error');
          throw new Error(res.error || 'Camera failed to initialize');
        }
        
        addDebugLog(`Camera initialized successfully with ${res.facingMode} camera`);
        
        // 2) Simplified approach - start preview and set as ready, then auto-fix if needed
        let previewSuccess = false;
        let lastError = null;
        
        // Method 1: Direct startPreview (optimistic approach)
        try {
          await cameraRef.current.startPreview(videoElement);
          addDebugLog('Direct preview method completed');
          previewSuccess = true;
        } catch (previewError) {
          addDebugLog(`Direct preview failed: ${previewError.message}`, 'warn');
          lastError = previewError;
        }
        
        // Method 2: If direct preview failed, try nuclear restart approach
        if (!previewSuccess) {
          addDebugLog('Trying nuclear restart approach for initial setup...');
          try {
            const nuclearResult = await cameraRef.current.performNuclearRestart();
            if (nuclearResult.success) {
              await cameraRef.current.startPreview(videoElement);
              addDebugLog('Nuclear restart preview method completed');
              previewSuccess = true;
            } else {
              addDebugLog(`Nuclear restart failed: ${nuclearResult.error}`, 'warn');
              lastError = new Error(nuclearResult.error);
            }
          } catch (nuclearError) {
            addDebugLog(`Nuclear restart error: ${nuclearError.message}`, 'warn');
            lastError = nuclearError;
          }
        }
        
        // Method 3: If both failed, try progressive nuclear restart
        if (!previewSuccess) {
          addDebugLog('Trying progressive nuclear restart approach...');
          try {
            const progressiveResult = await cameraRef.current.performProgressiveNuclearRestart();
            if (progressiveResult.success) {
              await cameraRef.current.startPreview(videoElement);
              addDebugLog('Progressive nuclear restart preview method completed');
              previewSuccess = true;
            } else {
              addDebugLog(`Progressive nuclear restart failed: ${progressiveResult.error}`, 'warn');
              lastError = new Error(progressiveResult.error);
            }
          } catch (progressiveError) {
            addDebugLog(`Progressive nuclear restart error: ${progressiveError.message}`, 'warn');
            lastError = progressiveError;
          }
        }
        
        // If all methods failed, throw the last error
        if (!previewSuccess) {
          addDebugLog('All preview methods failed', 'error');
          throw new Error(`Failed to start camera preview: ${lastError?.message || 'All methods failed'}`);
        }
        
        addDebugLog('Camera preview started successfully - will validate content after UI is ready');
        
        // 3) auto-detect black screen & fix
        try {
          await cameraRef.current.ensureNotBlack(videoElement);
          addDebugLog('Black screen check completed');
        } catch (blackScreenError) {
          addDebugLog(`Black screen check failed: ${blackScreenError.message}`, 'warn');
          // Don't throw here, just warn - camera might still work
        }
        
        // 4) add resilience handlers
        try {
          cleanupResilience = cameraRef.current.attachResilience(videoElement);
          addDebugLog('Resilience handlers attached');
        } catch (resilienceError) {
          addDebugLog(`Resilience setup failed: ${resilienceError.message}`, 'warn');
          // Don't throw here, just warn - camera can work without resilience
        }
        
        // Set camera as ready
        setCameraReady(true);
        setCameraError(null);
        
        // Update facing mode and mirror settings - ALWAYS MIRROR ALL CAMERAS
        const facingMode = cameraRef.current.constraints.video.facingMode;
        setCurrentFacingMode(facingMode);
        
        // Force mirror mode to always be true for all cameras
        setTimeout(() => {
          setMirrorMode(true);
          addDebugLog(`Camera ready with ${facingMode} camera, mirror: true (ALWAYS MIRRORED)`);
        }, 100);
        
        // Initialize GPS
        addDebugLog('Initializing GPS...');
        gpsRef.current = new GPSTracker();
        gpsRef.current.setWarehouseLocations(warehouseLocations);
        
        const locationResult = await gpsRef.current.getCurrentPosition();
        
        if (locationResult.success) {
          setLocationReady(true);
          setLocationError(null);
          setCurrentLocation(locationResult.location);
          addDebugLog(`GPS initialized successfully. Accuracy: ${locationResult.location.accuracy}m`);
          
          // Check warehouse proximity
          const proximityResult = gpsRef.current.checkProximity(warehouseLocations);
          setWarehouseProximity(proximityResult);
          addDebugLog(`Proximity check: ${proximityResult.isAtWarehouse ? 'At warehouse' : 'Not at warehouse'}`);
        } else {
          setLocationError(locationResult.error);
          addDebugLog(`GPS initialization failed: ${locationResult.error}`, 'error');
        }
        
      } catch (error) {
        addDebugLog(`Service initialization error: ${error.message}`, 'error');
        setCameraError(error.message);
        message.error(`Initialization failed: ${error.message}`);
      } finally {
        clearTimeout(absoluteTimeout);
        setLoading(false);
        addDebugLog('Service initialization completed');
      }
    };

    const cleanupServices = () => {
      addDebugLog('Cleaning up services...');
      if (cleanupResilience) {
        cleanupResilience();
      }
      if (cameraRef.current) {
        cameraRef.current.stopCamera();
      }
      if (gpsRef.current) {
        gpsRef.current.stopWatching();
      }
    };

    initServices();
    
    return cleanupServices;
  }, [warehouseLocations, attendanceType]);

  // Camera ready state management with automatic black screen detection and recovery
  useEffect(() => {
    if (cameraReady && cameraRef.current) {
      setRecoveryAttempts(0);
      setIsRecovering(false);
      setCameraState('ready');
      
      // Increment camera load counter for debugging
      setCameraLoadCount(prev => {
        const newCount = prev + 1;
        addDebugLog(`Camera load count: ${newCount}`, 'info');
        return newCount;
      });
      
      addDebugLog('Camera is ready - performing automatic black screen detection...');
      
      // Automatic black screen detection and recovery - optimized for speed
      const performBlackScreenCheck = async () => {
        try {
          // Reduced wait time for faster detection
          await new Promise(resolve => setTimeout(resolve, 200));
          
          if (!videoRef.current || !cameraReady) {
            addDebugLog('Black screen check cancelled - camera no longer ready', 'warn');
            return;
          }
          
          // Quick validation with shorter stabilization time
          const quickValidation = async () => {
            if (!videoRef.current) return false;
            
            // Shorter wait for quick detection
            await new Promise(resolve => setTimeout(resolve, 100));
            
            const hasValidContent = videoRef.current.videoWidth > 0 && 
                                   videoRef.current.videoHeight > 0 && 
                                   videoRef.current.readyState >= 2;
            
            addDebugLog(`Quick video validation: ${hasValidContent ? 'Valid' : 'Invalid'} (width: ${videoRef.current.videoWidth}, height: ${videoRef.current.videoHeight}, readyState: ${videoRef.current.readyState})`);
            return hasValidContent;
          };
          
          const hasValidContent = await quickValidation();
          
          if (!hasValidContent) {
            addDebugLog('Black screen detected - automatically triggering camera switch...', 'warn');
            
            // Automatically trigger switch camera to fix black screen
            try {
              const result = await cameraRef.current.switchCamera();
              
              if (result.success && videoRef.current) {
                addDebugLog('Automatic camera switch successful');
                
                // Use enhanced preview method
                await cameraRef.current.startPreview(videoRef.current);
                addDebugLog('Enhanced preview started after automatic switch');
                
                // Update facing mode and keep mirror mode always true
                const newFacingMode = cameraRef.current.constraints.video.facingMode;
                setCurrentFacingMode(newFacingMode);
                
                // Always keep mirror mode true for all cameras
                setMirrorMode(true);
                addDebugLog(`Mirror mode kept as true for ${newFacingMode} camera (ALWAYS MIRRORED)`);
                
                addDebugLog(`Automatic black screen recovery completed with ${newFacingMode} camera`);
                // Remove the success message to make transition more seamless
                // message.success('Camera automatically fixed!');
              } else {
                addDebugLog(`Automatic camera switch failed: ${result.error}`, 'error');
                setCameraError('Camera shows black screen. Please click the switch camera button.');
              }
            } catch (switchError) {
              addDebugLog(`Automatic camera switch error: ${switchError.message}`, 'error');
              setCameraError('Camera shows black screen. Please click the switch camera button.');
            }
          } else {
            addDebugLog('Black screen check passed - camera is working correctly');
          }
        } catch (error) {
          addDebugLog(`Black screen check error: ${error.message}`, 'warn');
          // Don't set error here, just log it
        }
      };
      
      // Perform the check after a delay
      performBlackScreenCheck();
    }
  }, [cameraReady]);


  const handleCapture = async () => {
    if (!cameraReady || !locationReady) {
      addDebugLog('Cannot capture: services not ready', 'error');
      message.error('Camera or GPS not ready. Please wait for initialization to complete.');
      return;
    }

    setCapturing(true);
    addDebugLog('Starting capture process...');
    
    try {
      // Step 1: Capture photo
      addDebugLog('Step 1: Capturing photo...');
      const photoResult = await cameraRef.current.capturePhoto(videoRef.current, mirrorMode);
      
      if (!photoResult.success) {
        throw new Error(`Photo capture failed: ${photoResult.error}`);
      }
      addDebugLog(`Photo captured successfully. Size: ${photoResult.dataUrl.length} chars`);

      // Step 2: Get current location
      addDebugLog('Step 2: Getting current location...');
      const locationResult = await gpsRef.current.getCurrentPosition();
      
      if (!locationResult.success) {
        throw new Error(`Location failed: ${locationResult.error}`);
      }
      addDebugLog(`Location obtained. Accuracy: ${locationResult.location.accuracy}m`);

      // Step 3: Validate photo
      addDebugLog('Step 3: Validating photo...');
      const photoValidation = cameraRef.current.validateLiveCapture(photoResult.metadata);
      addDebugLog(`Photo validation: ${photoValidation.isValid ? 'Valid' : 'Invalid'}`);
      
      // Step 4: Validate GPS
      addDebugLog('Step 4: Validating GPS...');
      const gpsValidation = gpsRef.current.validateAccuracy();
      const spoofingCheck = gpsRef.current.detectSpoofing();
      addDebugLog(`GPS validation: ${gpsValidation.isValid ? 'Valid' : 'Invalid'}`);
      addDebugLog(`Spoofing check: ${spoofingCheck.isSuspicious ? 'Suspicious' : 'Clean'}`);
      
      // Step 5: Check warehouse proximity
      addDebugLog('Step 5: Checking proximity...');
      const proximityCheck = gpsRef.current.checkProximity(warehouseLocations);
      addDebugLog(`Proximity check: ${proximityCheck.isAtWarehouse ? 'At warehouse' : 'Not at warehouse'}`);
      
      // Step 6: Compile validation results
      const validationResults = {
        photo: photoValidation,
        gps: gpsValidation,
        spoofing: spoofingCheck,
        proximity: proximityCheck,
        overall: {
          isValid: photoValidation.isValid && 
                   gpsValidation.isValid && 
                   !spoofingCheck.isSuspicious,
          score: Math.min(
            photoValidation.score || 100,
            spoofingCheck.confidence || 100,
            100 // Location is always valid (can be warehouse or job site)
          )
        }
      };

      // Step 7: Update state
      setCapturedPhoto(photoResult.dataUrl);
      setPhotoMetadata(photoResult.metadata);
      setValidationResults(validationResults);
      setCurrentLocation(locationResult.location);
      setWarehouseProximity(proximityCheck);
      
      addDebugLog(`Capture completed successfully. Overall score: ${validationResults.overall.score}`);
      
    } catch (error) {
      addDebugLog(`Capture error: ${error.message}`, 'error');
      console.error('Capture error:', error);
      Modal.error({
        title: 'Capture Failed',
        content: (
          <div>
            <p>{error.message || 'Failed to capture attendance. Please try again.'}</p>
            <details style={{ marginTop: 16 }}>
              <summary>Debug Information</summary>
              <pre style={{ fontSize: '12px', marginTop: 8 }}>
                {debugInfo.slice(-5).map(log => log.message).join('\n')}
              </pre>
            </details>
          </div>
        )
      });
    } finally {
      setCapturing(false);
      addDebugLog('Capture process completed');
    }
  };

  const handleSubmit = async () => {
    if (!capturedPhoto || !validationResults) {
      addDebugLog('Cannot submit: missing photo or validation results', 'error');
      message.error('No photo captured or validation incomplete.');
      return;
    }

    setSubmitting(true);
    addDebugLog('Starting submission process...');

    try {
      const employee = staffAuthManager.getCurrentEmployee();
      
      if (!employee) {
        throw new Error('No employee logged in');
      }
      
      const attendanceData = {
        uid: employee.uid,
        employeeId: employee.employeeId,
        type: attendanceType,
        timestamp: new Date(),
        location: currentLocation,
        isWarehouse: warehouseProximity?.isAtWarehouse || false,
        photoUrl: capturedPhoto,
        photoMetadata: photoMetadata,
        validationResults: validationResults,
        warehouseProximity: warehouseProximity
      };

      addDebugLog('Submitting attendance data...');
      // Submit to backend via attendance service
      const result = await attendanceManager.submitAttendance(attendanceData);

      if (result.success) {
        addDebugLog('Attendance submitted successfully');

        // Reset form
        setCapturedPhoto(null);
        setPhotoMetadata(null);
        setValidationResults(null);
        
        // Call parent callback if provided and navigate back to dashboard
        if (onCapture) {
          onCapture({
            ...attendanceData,
            attendanceId: result.attendanceId,
            status: result.status,
            backendValidation: result.validationResults,
            attendanceType: attendanceType // Pass the attendance type for success message
          });
        }
        
      } else {
        addDebugLog(`Submission failed: ${result.error}`, 'error');
        message.error({
          content: result.error || 'Failed to submit attendance. Please try again.',
          duration: 4,
        });
      }
    } catch (error) {
      addDebugLog(`Submit error: ${error.message}`, 'error');
      console.error('Submit attendance error:', error);
      message.error({
        content: error.message || 'An unexpected error occurred. Please try again.',
        duration: 4,
      });
    } finally {
      setSubmitting(false);
      addDebugLog('Submission process completed');
    }
  };

  const handleRetake = async () => {
    addDebugLog('Retaking photo - reinitializing camera...');
    
    // Set loading state
    setLoading(true);
    setCameraReady(false);
    setCameraError(null);
    
    // Reset UI state
    setCapturedPhoto(null);
    setPhotoMetadata(null);
    setValidationResults(null);
    
    // Safety timeout for retake operation
    const retakeTimeout = setTimeout(() => {
      addDebugLog('Retake operation safety timeout - stopping loading', 'warn');
      setLoading(false);
    }, 10000);
    
    try {
      addDebugLog('Step 1: Performing progressive nuclear restart for retake...');
      
      // Perform complete camera reinitialization using the same robust method as initial setup
      const restartResult = await cameraRef.current.performProgressiveNuclearRestart();
      
      if (!restartResult.success) {
        addDebugLog(`Progressive nuclear restart failed: ${restartResult.error}`, 'error');
        setCameraError(restartResult.error || 'Failed to reinitialize camera');
        return;
      }
      
      addDebugLog('Step 2: Starting enhanced preview after retake restart...');
      
      // Start preview with the reinitialized stream
      if (videoRef.current) {
        await cameraRef.current.startPreview(videoRef.current);
        addDebugLog('Enhanced preview started successfully after retake');
        
        // Step 3: Auto-detect and fix black screen issues
        addDebugLog('Step 3: Running black screen detection after retake...');
        await cameraRef.current.ensureNotBlack(videoRef.current);
        addDebugLog('Black screen check completed after retake');
        
        // Step 4: Set camera as ready and update states
        setCameraReady(true);
        setCameraError(null);
        
        // Update facing mode and keep mirror mode always true
        const facingMode = cameraRef.current.constraints.video.facingMode;
        setCurrentFacingMode(facingMode);
        setMirrorMode(true); // Always keep mirror mode true for all cameras
        
        addDebugLog(`Retake camera reinitialization completed successfully with ${facingMode} camera`);
        
      } else {
        addDebugLog('Video element not available for retake preview', 'error');
        setCameraError('Video element not available');
      }
      
    } catch (error) {
      addDebugLog(`Retake camera reinitialization error: ${error.message}`, 'error');
      setCameraError(`Failed to reinitialize camera: ${error.message}`);
      message.error('Failed to reinitialize camera. Please try again.');
    } finally {
      clearTimeout(retakeTimeout);
      setLoading(false);
      addDebugLog('Retake operation completed');
    }
  };

  const switchCamera = async () => {
    if (cameraRef.current) {
      setLoading(true);
      setCameraReady(false);
      addDebugLog('Enhanced camera switching...');
      
      // Safety timeout for camera switch
      const switchTimeout = setTimeout(() => {
        addDebugLog('Camera switch safety timeout - stopping loading', 'warn');
        setLoading(false);
      }, 8000);
      
      try {
        const result = await cameraRef.current.switchCamera();
        
        if (result.success && videoRef.current) {
          addDebugLog('Camera switched successfully, starting enhanced preview...');
          
          // Use enhanced preview method
          await cameraRef.current.startPreview(videoRef.current);
          addDebugLog('Enhanced preview started after switch');
          
          // Auto-detect black screen & fix
          await cameraRef.current.ensureNotBlack(videoRef.current);
          addDebugLog('Black screen check completed after switch');
          
          // Set camera as ready
          setCameraReady(true);
          setCameraError(null);
          
          // Update facing mode and keep mirror mode always true
          const newFacingMode = cameraRef.current.constraints.video.facingMode;
          setCurrentFacingMode(newFacingMode);
          
          // Always keep mirror mode true for all cameras
          setMirrorMode(true);
          addDebugLog(`Mirror mode kept as true for ${newFacingMode} camera (ALWAYS MIRRORED)`);
          
          addDebugLog(`Enhanced camera switch completed with ${newFacingMode} camera`);
        } else {
          addDebugLog(`Camera switch failed: ${result.error}`, 'error');
          setCameraError(result.error || 'Failed to switch camera');
        }
      } catch (error) {
        addDebugLog(`Camera switch error: ${error.message}`, 'error');
        setCameraError('Camera switch failed. Please try again.');
      } finally {
        clearTimeout(switchTimeout);
        setLoading(false);
      }
    }
  };


  const refreshLocation = async () => {
    setLoading(true);
    addDebugLog('Refreshing location...');
    try {
      const locationResult = await gpsRef.current.getCurrentPosition();
      
      if (locationResult.success) {
        setCurrentLocation(locationResult.location);
        const proximityResult = gpsRef.current.checkProximity(warehouseLocations);
        setWarehouseProximity(proximityResult);
        setLocationError(null);
        addDebugLog(`Location refreshed. Accuracy: ${locationResult.location.accuracy}m`);
      } else {
        setLocationError(locationResult.error);
        addDebugLog(`Location refresh failed: ${locationResult.error}`, 'error');
      }
    } catch (error) {
      setLocationError(error.error || 'Failed to refresh location');
      addDebugLog(`Location refresh error: ${error.message}`, 'error');
    }
    setLoading(false);
  };

  const retryCamera = async () => {
    setLoading(true);
    setCameraReady(false);
    setCameraError(null);
    addDebugLog('Retrying camera initialization...');
    
    // Safety timeout for camera retry
    const retryTimeout = setTimeout(() => {
      addDebugLog('Camera retry safety timeout - stopping loading', 'warn');
      setLoading(false);
    }, 10000);
    
    try {
      // Clean up existing camera first
      if (cameraRef.current) {
        cameraRef.current.stopCamera();
      }
      
      // Reset video element
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      
      // Wait a moment before retrying
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Create new camera instance
      cameraRef.current = new CameraCapture();
      
      // Try to initialize camera - always use front camera for both clock-in and clock-out
      const res = await cameraRef.current.initializeCamera('user');
      
      if (!res.success) {
        addDebugLog(`Camera retry failed: ${res.error}`, 'error');
        setCameraError(res.error || 'Camera retry failed');
        return;
      }
      
      addDebugLog(`Camera retry successful with ${res.facingMode} camera`);
      
      // Start preview
      try {
        await cameraRef.current.startPreview(videoRef.current);
        addDebugLog('Preview started after retry');
        
        // Set camera as ready
        setCameraReady(true);
        setCameraError(null);
        
        // Update facing mode and keep mirror mode always true
        const facingMode = cameraRef.current.constraints.video.facingMode;
        setCurrentFacingMode(facingMode);
        setMirrorMode(true); // Always keep mirror mode true for all cameras
        
        addDebugLog(`Camera retry completed successfully with ${facingMode} camera`);
        
      } catch (previewError) {
        addDebugLog(`Preview start failed after retry: ${previewError.message}`, 'error');
        setCameraError(`Failed to start camera preview: ${previewError.message}`);
      }
      
    } catch (error) {
      addDebugLog(`Camera retry error: ${error.message}`, 'error');
      setCameraError('Camera retry failed. Please check permissions and try again.');
    } finally {
      clearTimeout(retryTimeout);
      setLoading(false);
    }
  };


  if (loading && !cameraReady && !locationReady) {
    return (
      <div style={{ 
        height: '90vh', 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'center', 
        alignItems: 'center',
        padding: '5px'
      }}>
        <Spin size="large" />
        <div style={{ marginTop: 16, fontSize: '18px', fontWeight: 'bold' }}>
          Initializing Camera & GPS
        </div>
        <div style={{ marginTop: 8, color: '#666', textAlign: 'center' }}>
          Please allow camera and location permissions when prompted
        </div>
      </div>
    );
  }

  // Helper function to get status indicator
  const getStatusIndicator = (isReady, hasError, errorText) => {
    if (hasError) {
      return (
        <Tooltip title={errorText}>
          <span style={{ color: '#ff4d4f', fontSize: '16px' }}>●</span>
        </Tooltip>
      );
    }
    if (isReady) {
      return <span style={{ color: '#52c41a', fontSize: '16px' }}>●</span>;
    }
    return <span style={{ color: '#1890ff', fontSize: '16px' }}>●</span>;
  };

  // Helper function to get location text
  const getLocationText = () => {
    if (!warehouseProximity) return 'Location: Detecting...';
    
    if (warehouseProximity.isAtWarehouse) {
      return 'Location: Warehouse';
    } else if (warehouseProximity.nearestWarehouse) {
      const distance = warehouseProximity.nearestWarehouse.distanceKm;
      return `Location: Job site (${distance}km from warehouse)`;
    }
    return 'Location: Job site';
  };

  return (
    <div className="attendance-capture" style={{ 
      height: window.innerWidth > 768 ? '90vh' : '90vh', 
      display: 'flex', 
      flexDirection: 'column',
      padding: window.innerWidth > 768 ? '8px' : '4px',
      overflow: 'hidden',
      position: 'relative'
    }}>
      {/* Compact Status Row */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        padding: '5px 5px',
        background: '#f5f5f5',
        borderRadius: '0',
        marginBottom: '0',
        minHeight: '32px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Tooltip title={cameraError || (cameraReady ? 'Camera Ready' : 'Initializing Camera')}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <CameraOutlined />
              {getStatusIndicator(cameraReady, cameraError, cameraError)}
            </span>
          </Tooltip>
          
          <Tooltip title={locationError || (locationReady ? 'GPS Ready' : 'Initializing GPS')}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <EnvironmentOutlined />
              {getStatusIndicator(locationReady, locationError, locationError)}
            </span>
          </Tooltip>
          
          <span style={{ fontSize: '14px', color: '#666' }}>
            {getLocationText()}
          </span>
        </div>
        
        <Space size="small">
          <Button 
            size="small" 
            icon={<ReloadOutlined />} 
            onClick={refreshLocation}
            disabled={!locationReady}
            title="Refresh Location"
          />
          {cameraError && (
            <Button 
              size="small"
              icon={<CameraOutlined />} 
              onClick={retryCamera}
              disabled={loading}
              title="Retry Camera"
              type="primary"
            />
          )}
        </Space>
      </div>

      {/* Camera and Button Container - Unified block */}
      <div style={{ 
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: window.innerWidth > 768 ? 'center' : 'flex-start',
        minHeight: 0, // Allow flex shrinking
        paddingBottom: '8px'
      }}>
        <div style={{
          width: '100%',
          maxWidth: window.innerWidth > 768 ? '640px' : '95%', // Desktop: 640px, Mobile: 95% for better fit
          display: 'flex',
          flexDirection: 'column',
          background: '#fff',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.1)',
          maxHeight: '100%' // Prevent overflow
        }}>
            {/* Camera Preview */}
            <div style={{
              width: '100%',
              aspectRatio: window.innerWidth > 768 ? '16/9' : '9/16', // Desktop: 16:9, Mobile: 9:16 (portrait)
              position: 'relative',
              background: '#000',
              maxHeight: window.innerWidth > 768 ? 'none' : '70vh' // Limit height on mobile but allow more space for portrait
            }}>
              {!capturedPhoto ? (
                <>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      backgroundColor: '#000',
                      transform: mirrorMode ? 'scaleX(-1)' : 'none',
                      transition: 'transform 0.3s ease'
                    }}
                  />
                  {!cameraReady && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(0,0,0,0.7)',
                      color: 'white'
                    }}>
                      <Spin size="large" />
                    </div>
                  )}
                  
                  {/* Capture Overlay - Dim video and show capturing message */}
                  {capturing && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(0,0,0,0.7)',
                      color: 'white',
                      zIndex: 5
                    }}>
                      <Spin size="large" style={{ marginBottom: '16px' }} />
                      <div style={{
                        fontSize: window.innerWidth > 768 ? '18px' : '16px',
                        fontWeight: '600',
                        textAlign: 'center',
                        animation: 'pulse 1.5s ease-in-out infinite'
                      }}>
                        📸 Capturing your attendance photo...
                      </div>
                    </div>
                  )}
                </>
            ) : (
              <>
                <Image
                  src={capturedPhoto}
                  alt="Captured attendance photo"
                  style={{ 
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transform: mirrorMode ? 'scaleX(-1)' : 'none',
                    transition: 'transform 0.3s ease'
                  }}
                  preview={false}
                />
                
                {/* Validation Results - Overlay */}
                {validationResults && !validationResults.overall.isValid && (
                  <div style={{ 
                    position: 'absolute',
                    bottom: window.innerWidth <= 768 ? '80px' : '8px', // Move up on mobile to avoid button overlap
                    left: '8px',
                    right: '8px',
                    padding: '8px 12px',
                    background: 'rgba(255, 242, 232, 0.95)',
                    border: '1px solid #ffbb96',
                    borderRadius: '4px',
                    fontSize: '12px',
                    color: '#d46b08',
                    textAlign: 'center'
                  }}>
                    Validation Score: {validationResults.overall.score}% - Please retake photo
                  </div>
                )}
              </>
            )}

            {/* Action Buttons - Inside camera view at bottom */}
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              background: 'transparent',
              padding: window.innerWidth <= 768 ? '24px 12px 16px 12px' : '12px 16px',
              display: 'flex',
              justifyContent: 'center',
              gap: '12px',
              zIndex: 10
            }}>
              {!capturedPhoto ? (
                <Button
                  type="primary"
                  size="large"
                  icon={<CameraOutlined />}
                  onClick={handleCapture}
                  loading={capturing}
                  disabled={!cameraReady || !locationReady}
                  style={{ 
                    minWidth: window.innerWidth < 768 ? '180px' : '140px',
                    height: window.innerWidth < 768 ? '52px' : '40px',
                    fontSize: window.innerWidth < 768 ? '18px' : '14px',
                    fontWeight: '600',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {capturing ? 'Capturing...' : `${attendanceType === 'clock-in' ? 'Clock In' : 'Clock Out'}`}
                </Button>
              ) : (
                <Space size="middle" style={{ width: '100%', justifyContent: 'center' }}>
                  <Button 
                    size={window.innerWidth < 768 ? 'large' : 'middle'}
                    onClick={handleRetake}
                    style={{ 
                      height: window.innerWidth < 768 ? '52px' : '40px',
                      fontSize: window.innerWidth < 768 ? '16px' : '14px',
                      minWidth: window.innerWidth < 768 ? '110px' : '100px',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: window.innerWidth <= 768 ? 'rgba(255,255,255,0.9)' : undefined,
                      color: window.innerWidth <= 768 ? '#000' : undefined
                    }}
                  >
                    Retake
                  </Button>
                  <Button
                    type="primary"
                    size={window.innerWidth < 768 ? 'large' : 'middle'}
                    icon={<CheckCircleOutlined />}
                    onClick={handleSubmit}
                    loading={submitting}
                    disabled={!validationResults?.overall.isValid || submitting}
                    style={{ 
                      height: window.innerWidth < 768 ? '52px' : '40px',
                      fontSize: window.innerWidth < 768 ? '16px' : '14px',
                      minWidth: window.innerWidth < 768 ? '140px' : '140px',
                      fontWeight: '600',
                      borderRadius: '8px',
                      boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {submitting ? 'Submitting...' : 'Submit'}
                  </Button>
                </Space>
              )}
            </div>
          </div>

          {/* Desktop only: Additional spacing below camera container */}
          {window.innerWidth > 768 && (
            <div style={{ height: '8px' }} />
          )}
        </div>
      </div>

      {/* Full Screen Submission Loading Overlay */}
      {submitting && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          backdropFilter: 'blur(2px)'
        }}>
          <Spin size="large" style={{ marginBottom: '24px' }} />
          <div style={{
            color: 'white',
            fontSize: window.innerWidth > 768 ? '20px' : '18px',
            fontWeight: '600',
            textAlign: 'center',
            animation: 'pulse 1.5s ease-in-out infinite'
          }}>
            Submitting attendance...
          </div>
          <div style={{
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: window.innerWidth > 768 ? '14px' : '12px',
            textAlign: 'center',
            marginTop: '8px'
          }}>
            Please wait, do not close this page
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceCapture;
